import { Spacing } from '@persona-id/design-tokens';
import { capitalize } from 'lodash/fp';
import { ChangeEvent, PropsWithChildren, useCallback } from 'react';
import { batch, useDispatch } from 'react-redux';
import { change, EventOrValueHandler, Field } from 'redux-form';
import styled from 'styled-components';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { COMPONENT_SCHEMA } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import { UiComponentConfigType } from 'lib/interfaces/ui-components';

type ButtonConfigTypeEditorProps = {
  basePath: string;
  stepBasePath: string;
  editable: boolean;
  isNewComponent: boolean;
  currentStep: InquiryTemplateVersionStepUi;
};

type SelectableButtonType =
  | UiComponentConfigType.ButtonAction
  | UiComponentConfigType.ButtonCancel
  | UiComponentConfigType.ButtonComplete
  | UiComponentConfigType.ButtonSubmit;

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

const StyledSelectField = styled(SelectField)`
  button {
    margin-bottom: 0;
  }
  margin-bottom: ${Spacing[3]};
`;

const ButtonComponentConfigAddOn = ({
  basePath,
  stepBasePath,
  editable,
  isNewComponent,
  currentStep,
  children,
}: PropsWithChildren<ButtonConfigTypeEditorProps>) => {
  const dispatch = useDispatch();

  const onButtonTypeChange = useCallback(
    (e) => {
      // Because we implemented a custom `onChange` for the `SelectField` below,
      // this method actually gets called twice, once with an Event and once with
      // a string. We need to account for both
      if (typeof e === 'object') {
        return;
      }
      const buttonType: SelectableButtonType = e;
      const buttonComponentConfig = COMPONENT_SCHEMA[buttonType].config.config;

      batch(() => {
        if (
          [UiComponentConfigType.ButtonAction, UiComponentConfigType.ButtonSubmit].includes(
            buttonType,
          )
        ) {
          dispatch(
            change(
              form,
              `steps.${stepBasePath}.config.statuses`,
              // Remove all terminal statuses
              currentStep.config.statuses?.filter(({ terminal }) => !terminal) ?? [],
            ),
          );
        } else if (
          [UiComponentConfigType.ButtonComplete, UiComponentConfigType.ButtonCancel].includes(
            buttonType,
          )
        ) {
          dispatch(
            change(form, `steps.${stepBasePath}.config.statuses`, [
              // Replace with one terminal status
              {
                name: 'completed',
                initial: false,
                terminal: true,
              },
            ]),
          );
        }
        dispatch(change(form, `steps.${basePath}.config`, buttonComponentConfig));
      });
    },
    [basePath, currentStep.config.statuses, dispatch, stepBasePath],
  ) as EventOrValueHandler<ChangeEvent<SelectableButtonType>>;

  return (
    <>
      <Field
        name={`${basePath}.type`}
        component={StyledSelectField}
        disabled={!editable || !isNewComponent}
        onChange={onButtonTypeChange}
        normalize={(e: ChangeEvent<HTMLInputElement> | SelectableButtonType) => {
          // Because we implemented a custom `onChange` for the `SelectField` below,
          // this method actually gets called twice, once with an Event and once with
          // a string. We need to account for both
          if (typeof e === 'object') {
            return e.target.value;
          }
          return e;
        }}
        size='sm'
        label='Button type'
      >
        {/**
         * TODO: Add a help center article covering the usages for
         * these button types + a tooltip around this select linking
         */}
        {[
          UiComponentConfigType.ButtonAction,
          UiComponentConfigType.ButtonCancel,
          UiComponentConfigType.ButtonComplete,
          UiComponentConfigType.ButtonSubmit,
        ].map((buttonConfigType) => (
          <Option key={buttonConfigType} value={buttonConfigType}>
            {capitalize(buttonConfigType.replace('button_', ''))}
          </Option>
        ))}
      </Field>
      {children}
    </>
  );
};

export default ButtonComponentConfigAddOn;
